/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */

import { getLogger, ILogger } from "@nuance/hux-diagnostics";
import { CmdBuilder, IViewModel } from "@nuance/hap-components";
import {
    ControlType,
    IHeading,
    ISectionHeading,
    ISettingGroupDefinition
} from "@nuance/hierarchical-settings-ui-domain";
import { IFieldDefinitionsPanelData, IFieldDefinitionsPanelProps } from "./Panels";
import { DomainDataMapperFactory } from "./DomainDataMapper/DomainDataMapperFactory";

let logger: ILogger;
/**
 * FieldDefinitions state
 */
export interface IFieldDefinitionsState {
    /**
     * Index of the currently selected item
     */
    currentFieldIndex: number;
    isFieldDefinitionPanelOpen: boolean;
    canMoveUp: boolean;
    canMoveDown: boolean;
    settingDefinition: ISettingGroupDefinition;
    currentItem?: IFieldDefinitionsPanelProps;
}

export class FieldDefinitionsCommands {
    constructor() {
        logger = getLogger();
        const cb = new CmdBuilder();
        const cmds = {
            fieldSelectionChanged: cb.bindCmd(this.fieldSelectionChangedHandler),
            cancelFieldDefinitionPanel: cb.bindCmdNP(this.cancelFieldDefinitionPanelHandler),
            moveControlDown: cb.bindCmdNP(this.moveControlDownHandler),
            moveControlUp: cb.bindCmdNP(this.moveControlUpHandler),
            addControl: cb.bindCmd(this.addControlHandler),
            editControl: cb.bindCmdNP(this.editControlHandler),
            savePanelData: cb.bindCmd(this.savePanelDataHandler),
            dragAndDropControl: cb.bindCmd(this.dragAndDropHandler)
        };

        this.Cmds = cmds;
        this.Reducer = cb.buildReducer(cmds);
    }

    readonly Cmds;
    readonly Reducer;

    ValidateSettingName = (
        state: IFieldDefinitionsState,
        proposedName: string,
        originalName: string
    ): string => {
        if (proposedName === originalName) {
            return "";
        }

        // check fields
        if (
            state.settingDefinition.Fields.find(i => i.SettingName === proposedName) !== undefined
        ) {
            return "Name is already in use by setting.";
        }

        // check headings
        if (
            state.settingDefinition.UI.Controls.find(
                i =>
                    i.ControlDiscriminator === ControlType.Heading &&
                    (i as IHeading).HeadingId === proposedName
            ) !== undefined
        ) {
            return "Name already in use by heading.";
        }

        // check section headings
        if (
            state.settingDefinition.UI.Controls.find(
                i =>
                    i.ControlDiscriminator === ControlType.SectionHeading &&
                    (i as ISectionHeading).SectionHeadingId === proposedName
            ) !== undefined
        ) {
            return "Name already in use by section.";
        }

        return "";
    };

    /////////////////////////////////////////////////////////////////
    // Command handlers
    /////////////////////////////////////////////////////////////////

    fieldSelectionChangedHandler = (state: IFieldDefinitionsState, fieldIndex: number) => {
        state.currentFieldIndex = fieldIndex;
        state.canMoveUp = fieldIndex !== -1 && fieldIndex !== 0;
        state.canMoveDown =
            fieldIndex !== -1 && fieldIndex < state.settingDefinition.UI.Controls.length - 1;
        return state;
    };

    addControlHandler = (state: IFieldDefinitionsState, controlType: ControlType) => {
        logger.trackEvent("Add field", {
            controlType: ControlType[controlType]
        });

        const mapper = DomainDataMapperFactory.Create(state.settingDefinition, controlType);

        state.currentItem = mapper.BuildPanelPropsForNewItem();
        state.isFieldDefinitionPanelOpen = true;
        return state;
    };

    editControlHandler = (state: IFieldDefinitionsState): IFieldDefinitionsState => {
        if (state.currentFieldIndex !== -1) {
            const uiCtrl = state.settingDefinition.UI.Controls[state.currentFieldIndex];

            logger.trackEvent("Edit field", {
                controlType: ControlType[uiCtrl.ControlDiscriminator],
                ctrlGuid: uiCtrl.CtrlGuid
            });

            const mapper = DomainDataMapperFactory.Create(state.settingDefinition, uiCtrl.CtrlGuid);
            state.currentItem = mapper.BuildPanelPropsForExistingItem(uiCtrl.CtrlGuid);
            state.isFieldDefinitionPanelOpen = true;
        } else {
            logger.logWarning("Attempting to edit control but index is -1");
        }

        return state;
    };

    savePanelDataHandler = (
        state: IFieldDefinitionsState,
        panelData: IFieldDefinitionsPanelData
    ) => {
        logger.trackEvent("Save field definition", {
            ctrlGuid: panelData.ctrlGuid,
            controlType: ControlType[panelData.definitionType]
        });

        const isNew = panelData.ctrlGuid === undefined;

        const mapper =
            panelData.ctrlGuid === undefined
                ? DomainDataMapperFactory.Create(state.settingDefinition, panelData.definitionType)
                : DomainDataMapperFactory.Create(state.settingDefinition, panelData.ctrlGuid);

        state.settingDefinition = mapper.SaveDataToDefinition(panelData);
        state.isFieldDefinitionPanelOpen = false;
        state.currentItem = undefined;

        if (state.currentFieldIndex !== -1 && isNew) {
            const indexNewDefinition = state.settingDefinition.UI.Controls.length - 1;

            //Next two lines place new control under the currently selected control
            const cutOut = state.settingDefinition.UI.Controls.splice(indexNewDefinition, 1)[0];
            state.settingDefinition.UI.Controls.splice(state.currentFieldIndex + 1, 0, cutOut);
        } else if (
            /** Checks if the newly added control is the first Section */
            state.settingDefinition.UI.Controls.filter(
                control => control.ControlDiscriminator === 6
            ).length === 1
        ) {
            /** Place the new section as the first control */
            const newControl = state.settingDefinition.UI.Controls.slice(-1)[0];
            state.settingDefinition.UI.Controls.pop();
            state.settingDefinition.UI.Controls.unshift(newControl);
        }

        return state;
    };

    cancelFieldDefinitionPanelHandler = (state: IFieldDefinitionsState): IFieldDefinitionsState => {
        logger.trackEvent("Cancel editing field definition", {
            ctrlGuid: state.currentItem?.data.ctrlGuid,
            controlType:
                state.currentItem !== undefined &&
                ControlType[state.currentItem?.data.definitionType]
        });
        state.isFieldDefinitionPanelOpen = false;
        state.currentItem = undefined;
        return state;
    };

    moveControlDownHandler = (state: IFieldDefinitionsState): IFieldDefinitionsState => {
        logger.trackEvent("Move field definition down");
        const controls = state.settingDefinition.UI.Controls;
        const temp = controls[state.currentFieldIndex];
        controls[state.currentFieldIndex] = controls[state.currentFieldIndex + 1];
        controls[state.currentFieldIndex + 1] = temp;
        return state;
    };

    moveControlUpHandler = (state: IFieldDefinitionsState): IFieldDefinitionsState => {
        logger.trackEvent("Move field definition up");
        const controls = state.settingDefinition.UI.Controls;
        const temp = controls[state.currentFieldIndex];
        controls[state.currentFieldIndex] = controls[state.currentFieldIndex - 1];
        controls[state.currentFieldIndex - 1] = temp;
        return state;
    };

    dragAndDropHandler = (
        state: IFieldDefinitionsState,
        newIndex: number
    ): IFieldDefinitionsState => {
        logger.trackEvent("Drag and drop field definition");

        //Next two lines put the array of controls into the new order
        const cutOut = state.settingDefinition.UI.Controls.splice(state.currentFieldIndex, 1)[0];
        state.settingDefinition.UI.Controls.splice(newIndex, 0, cutOut);

        return state;
    };
}

type IFieldDefinitionsCmds = FieldDefinitionsCommands["Cmds"];

export type IFieldDefinitionsViewModel = IViewModel<IFieldDefinitionsState, IFieldDefinitionsCmds>;
